/**
 * Occasions (blueprint S20, M10, M15, A11): public holidays, retail and
 * cultural moments, industry moments and the workspace's own events, shown on
 * the calendar next to posts. A definition is timeless; an occasion is a
 * definition resolved to one date in one year.
 */

/** Where an occasion comes from. Global fills the label, regional outlines it, custom carries a user icon. */
export type OccasionScope = 'global' | 'regional' | 'industry' | 'custom';

export type OccasionKind = 'public_holiday' | 'observance' | 'retail' | 'cultural' | 'industry' | 'custom';

/** How a definition lands on a date each year. */
export type OccasionRecurrence =
  /** Same day every year (25 December). */
  | { type: 'fixed'; month: number; day: number }
  /** The n-th weekday of a month; n < 0 counts from the end (last Sunday of March). weekday: 0 = Sunday. */
  | { type: 'nth_weekday'; month: number; weekday: number; n: number }
  /** Days relative to Western Easter Sunday (Good Friday = -2). */
  | { type: 'easter_offset'; days: number }
  /** Dates that follow another calendar (Diwali, Eid): one entry per year, YYYY-MM-DD. */
  | { type: 'table'; dates: Record<string, string> }
  /** A one-off date, YYYY-MM-DD. */
  | { type: 'once'; date: string };

export interface OccasionDefinition {
  /** Stable id, e.g. `nl-kings-day`, `global-black-friday`, or a custom uuid. */
  id: string;
  name: string;
  emoji: string;
  scope: OccasionScope;
  kind: OccasionKind;
  /** ISO 3166-1 alpha-2 codes. Empty means everywhere. */
  countries: string[];
  /** Countries an everywhere occasion is not shown in, because they keep their own version of it (PC-34c). */
  excludeCountries?: string[];
  /** Subdivision codes (e.g. `DE-BY`) when the occasion is regional inside a country. */
  regions?: string[];
  /** Workspace industries this matters to. Empty means every industry. */
  industries?: string[];
  recurrence: OccasionRecurrence;
  /**
   * The day a country keeps instead, by country then year (YYYY-MM-DD): India
   * usually sights the moon a day after the Umm al-Qura table (PC-13). A year
   * not listed falls back to `recurrence`.
   */
  countryDates?: Record<string, Record<string, string>>;
  /** How many days before the date a post should go out. */
  leadDays: number;
  /** One line on why a small business should care, in English; the UI may translate. */
  whyItMatters?: string;
  /** Text the AI should know (a closure, an offer). Custom occasions only. */
  noteForAi?: string;
  /** Occasion runs for several days (Diwali, Oktoberfest). */
  durationDays?: number;
  /** curated: hand-written; imported: the public-holiday feed; custom: the workspace's own. */
  source?: 'curated' | 'imported' | 'custom';
  /** The holiday's own name in the country's language, when it differs. */
  localName?: string;
}

/** A definition resolved to a concrete date. */
export interface Occasion extends OccasionDefinition {
  /** YYYY-MM-DD in the workspace's calendar. */
  date: string;
  /** Last day, YYYY-MM-DD, when durationDays > 1; else equal to date. */
  endDate: string;
  /** Suggested day to post, YYYY-MM-DD (date minus leadDays, never in the past when resolving for today). */
  suggestedDate: string;
}

export interface OccasionSettings {
  /** ISO 3166-1 alpha-2, e.g. `NL`. */
  country: string;
  /** Subdivision code, e.g. `DE-BY`, or null. */
  region: string | null;
  /** Workspace industry key, or null. */
  industry: string | null;
  /** Scopes shown on the calendar. */
  enabledScopes: OccasionScope[];
  /** Blueprint A11: pre-draft posts 7 days ahead for matched occasions. */
  autoPlan: boolean;
}

export const OCCASION_SCOPES: readonly OccasionScope[] = ['global', 'regional', 'industry', 'custom'];

/**
 * The defaults. The country is the last fallback only, after the brand's
 * time zone and the account's billing country (OccasionsService): India, the
 * home market, rather than a Dutch calendar for a brand nobody placed.
 */
export const DEFAULT_OCCASION_SETTINGS: OccasionSettings = {
  country: 'IN',
  region: null,
  industry: null,
  enabledScopes: ['global', 'regional', 'industry', 'custom'],
  autoPlan: false,
};

/** Countries the occasion dataset covers, Europe-first then India. */
export const OCCASION_COUNTRIES = ['NL', 'DE', 'FR', 'ES', 'IT', 'BE', 'PT', 'IE', 'GB', 'IN'] as const;
export type OccasionCountry = (typeof OCCASION_COUNTRIES)[number];

/** Input for creating a custom occasion (M15). */
export interface CustomOccasionInput {
  name: string;
  emoji: string;
  recurrence: OccasionRecurrence;
  noteForAi?: string;
  durationDays?: number;
  leadDays?: number;
}
